/**
 * ReleaseOverbookButton
 * ──────────────────────────────────────────────────────────────────────
 * Tiny inline action that flips an appointment's `is_overbooked` flag
 * back to false via `POST /api/scheduler/overbooking/{id}/release`.
 *
 * Behaviour:
 *   • Default `cancel: true` — releasing also cancels the booking. Pass
 *     `cancel={false}` when the underlying cap was raised after the fact.
 *   • Confirms via a tiny inline two-step (click → "Confirm release") so
 *     a misclick on a busy calendar never silently cancels an appointment.
 *   • Surfaces backend decision codes via `mapOverbookError` (shared
 *     with OverbookDialog) so the user sees a precise reason on 4xx.
 */
import { useState, useCallback } from 'react';
import { Button } from '../../../ui/button';
import { Loader2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import * as schedulerApi from '../../../../lib/api/schedulerApi';
import { mapOverbookError } from './OverbookDialog';

const ReleaseOverbookButton = ({
  bookingId,
  cancel = true,
  reason = '',
  onReleased,
  size = 'xs',
  testId = 'release-overbook-btn',
}) => {
  const [stage, setStage] = useState('idle');   // idle | confirm | working
  const sizeClass = size === 'xs' ? 'h-5 px-1.5 text-[9px]' : 'h-6 px-2 text-[10px]';

  const doRelease = useCallback(async () => {
    setStage('working');
    const res = await schedulerApi.releaseOverbooking(bookingId, {
      cancel,
      reason: reason || (cancel ? 'overbook released (cancelled)' : 'overbook released'),
    });
    if (res.ok) {
      toast.success(cancel ? 'Overbook released — appointment cancelled.' : 'Overbook flag cleared.');
      onReleased && onReleased(res.data?.appointment);
      setStage('idle');
    } else {
      const mapped = mapOverbookError(res.error);
      toast.error(mapped.message);
      setStage('idle');
    }
  }, [bookingId, cancel, reason, onReleased]);

  if (stage === 'confirm') {
    return (
      <span className="inline-flex items-center gap-0.5">
        <Button
          variant="outline"
          className={`${sizeClass} border-red-300 text-red-700 hover:bg-red-50`}
          onClick={(e) => { e.stopPropagation(); doRelease(); }}
          data-testid={`${testId}-confirm`}
        >
          Confirm release
        </Button>
        <Button
          variant="ghost"
          className={`${sizeClass} text-slate-500`}
          onClick={(e) => { e.stopPropagation(); setStage('idle'); }}
          data-testid={`${testId}-cancel`}
        >
          ×
        </Button>
      </span>
    );
  }

  return (
    <Button
      variant="outline"
      className={`${sizeClass} border-amber-300 text-amber-700 hover:bg-amber-50`}
      onClick={(e) => { e.stopPropagation(); setStage('confirm'); }}
      disabled={stage === 'working'}
      data-testid={testId}
    >
      {stage === 'working'
        ? <Loader2 className="h-2.5 w-2.5 animate-spin" />
        : <><Undo2 className="h-2.5 w-2.5 mr-0.5" /> Release</>}
    </Button>
  );
};

export default ReleaseOverbookButton;
